import { useState } from "react";
import { ChevronLeftIcon } from "@heroicons/react/24/outline";
import { CircularProgress } from "@mui/material";
import PinInput from "react-pin-input";
import Button from "../Button";
import ShowToast from "../alerts/all_toasts";
import BaseService from "../../helpers/baseServices";
import { RouteConstants } from "../../helpers/RouteConstants";

type OtpPurpose = "LOGIN" | "PASSWORD_RESET";

interface Props {
  identifier: string;
  purpose: OtpPurpose;
  title: string;
  subtitle: string;
  submitLabel: string;
  onBack: () => void;
  onSuccess: (payload: any) => void;
}

const OtpVerifyStep = ({
  identifier,
  purpose,
  title,
  subtitle,
  submitLabel,
  onBack,
  onSuccess,
}: Props) => {
  const [code, setCode] = useState("");
  const [newPassword, setNewPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [wrongPasswords, setWrongPasswords] = useState(false);
  const [isBusy, setIsBusy] = useState(false);

  const handleSubmit = async () => {
    if (!code || !newPassword || !confirmPassword) {
      ShowToast.error_toast("Fill in the code and your new password");
      return;
    }
    if (newPassword !== confirmPassword) {
      setWrongPasswords(true);
      return;
    }
    if (newPassword.length < 8) {
      ShowToast.error_toast("Password must be at least 8 characters");
      return;
    }
    setWrongPasswords(false);
    setIsBusy(true);
    try {
      const response = await BaseService.post_api(RouteConstants.OTP_VERIFY, {
        identifier,
        code,
        purpose,
        new_password: newPassword,
      });
      if (response?.data?.payload?.status === "OK") {
        onSuccess(response.data.payload);
      } else {
        ShowToast.error_toast("Verification did not complete. Try again.");
      }
    } catch (error: any) {
      ShowToast.error_toast(error?.response?.data?.message);
    } finally {
      setIsBusy(false);
    }
  };

  const handleResend = async () => {
    setIsBusy(true);
    try {
      await BaseService.post_api(RouteConstants.OTP_REQUEST, {
        identifier,
        purpose,
      });
      ShowToast.success_toast("Verification code sent");
    } catch (error: any) {
      ShowToast.error_toast(error?.response?.data?.message);
    } finally {
      setIsBusy(false);
    }
  };

  return (
    <div>
      <button onClick={onBack}>
        <ChevronLeftIcon className="w-6 h-6" />
      </button>
      <div className="w-full mt-5">
        <div className="header-text-3">{title}</div>
        <div className="body-text-2 pt-1">{subtitle}</div>
      </div>

      <div className="mt-[3rem]">
        <PinInput
          length={6}
          initialValue=""
          secret
          secretDelay={100}
          type="custom"
          inputMode="number"
          inputStyle={{
            borderColor: "#939291",
            backgroundColor: "#fff",
            borderRadius: "8px",
          }}
          inputFocusStyle={{ borderColor: "#FF6200" }}
          onComplete={(value) => setCode(value)}
          autoSelect={true}
          regexCriteria={/^[ A-Za-z0-9_@./#&+-]*$/}
        />
      </div>

      <div className="flex flex-col mt-8 relative">
        <small className="mb-2">New password</small>
        <input
          className="input"
          type="password"
          value={newPassword}
          onChange={(e) => setNewPassword(e.target.value)}
          placeholder="At least 8 characters"
        />
      </div>

      <div className="flex flex-col mt-4 relative">
        <small className="mb-2">Confirm password</small>
        <input
          className="input"
          type="password"
          value={confirmPassword}
          onChange={(e) => setConfirmPassword(e.target.value)}
          placeholder="Confirm your new password"
        />
      </div>
      {wrongPasswords && (
        <div className="text-red-500 mt-2">Passwords don't match!</div>
      )}

      <div className="mt-5">
        {!isBusy && (
          <p className="flex items-center gap-2">
            Didn't receive code?{" "}
            <span className="btn-subtle" onClick={handleResend}>
              Resend.
            </span>
          </p>
        )}
      </div>

      <div className="mt-[50px] flex justify-center items-center">
        <Button width="w-full" disabled={isBusy} onClick={handleSubmit}>
          {isBusy ? (
            <CircularProgress size={24} sx={{ color: "white" }} />
          ) : (
            <span>{submitLabel}</span>
          )}
        </Button>
      </div>
    </div>
  );
};

export default OtpVerifyStep;
